import { t } from '@exam-prep/i18n';
import { Button, LoaderOverlay } from '@exam-prep/ui';
import { ApiError } from '../lib/api';

/** The branded loader, centred on a full-screen overlay that blocks the screen while it shows. */
export function Loading() {
  return (
    <LoaderOverlay label={t('miniapp.loading')}>
      <div className="flex flex-col items-center gap-3 p-10 text-center" role="status">
        <img
          src="/brand/logo-128.webp"
          alt=""
          width={64}
          height={64}
          className="brand-halo brand-float size-16"
        />
        <p className="text-sm text-muted-foreground">{t('miniapp.loading')}</p>
      </div>
    </LoaderOverlay>
  );
}

/** The 401 screen: not signed in or the session expired, with a way back in and a way home. */
export function UnauthorizedScreen({
  onSignIn,
  onHome,
}: {
  onSignIn: () => void;
  onHome: () => void;
}) {
  return (
    <main
      role="alert"
      className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 p-6 text-center"
    >
      <img
        src="/brand/logo-128.webp"
        alt=""
        width={64}
        height={64}
        className="brand-halo size-16"
      />
      <p className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
        {t('miniapp.unauthorized.code')}
      </p>
      <h1 className="font-display text-xl font-bold">{t('miniapp.unauthorized.title')}</h1>
      <p className="text-sm text-muted-foreground">{t('miniapp.unauthorized.body')}</p>
      <div className="flex w-full flex-col gap-2">
        <Button size="lg" className="w-full" onClick={onSignIn}>
          {t('miniapp.unauthorized.sign_in')}
        </Button>
        <Button variant="outline" size="lg" className="w-full" onClick={onHome}>
          {t('miniapp.unauthorized.home')}
        </Button>
      </div>
    </main>
  );
}

export function ErrorBox({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg =
    error instanceof ApiError && error.status === 401
      ? t('miniapp.session_expired')
      : error instanceof ApiError && error.status < 500
        ? error.message
        : t('miniapp.error');
  return (
    <div
      role="alert"
      className="m-4 space-y-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-center text-sm text-red-800"
    >
      <p>{msg}</p>
      {retry ? (
        <Button variant="outline" onClick={retry}>
          {t('miniapp.retry')}
        </Button>
      ) : null}
    </div>
  );
}
